import React, { useEffect, useState } from 'react';
import './WebsiteHome.css';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import {
  handleGoogleLogin,
  google_sign_client_id,
  isAuthenticated,
} from '../apiservices/axiosservice/authentication';
import { setUserPermissions } from '../redux/slices/access/permissionSlice';
import { fetchCountry } from '../apiservices/axiosservice/gameservice';
import Loading from '../components/admincomponents/components/Loading';

const WebsiteHome: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [countryName, setCountryName] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchCountry()
      .then((country) => setCountryName(country))
      .catch(() => setCountryName(''));
  }, []);

  const setUserAuthentication = (data: any) => {
    dispatch(
      setUserPermissions({
        privileges: data.permissions,
        isAuthenticated: isAuthenticated(),
        isRehydrating: false,
      })
    );
  };

  const handleLoginFailure = () => {
    setErrorMessage('Google authentication failed. Please try again.');
  };

  const handleLoginSuccess = async (response: any) => {
    try {
      setIsLoading(true);
      const serverResponse = await handleGoogleLogin({
        token: response.credential,
        countryName,
      });
      setIsLoading(false);
      if (serverResponse.data) {
        setUserAuthentication(serverResponse.data);
        const { loginMode, profiled } = serverResponse.data.user;
        if (loginMode === 'google') {
          navigate(profiled ? '/dashboard/home' : '/profile-subscriber');
        }
      }
    } catch (error: any) {
      setIsLoading(false);
      setErrorMessage(
        error?.message || 'Google authentication failed. Please try again.'
      );
    }
  };

  return (
    <div className="wh-page">
      {/* Floating decorative icons */}
      <div className="wh-floating-icons">
        <span className="wh-icon wh-icon-trophy">🏆</span>
        <span className="wh-icon wh-icon-gamepad">🎮</span>
      </div>

      {/* Decorative grid blocks */}
      <div className="wh-grid-blocks">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className={`wh-block wh-block-${i}`} />
        ))}
      </div>

      {/* Main content */}
      <div className="wh-content">
        <img src="/funhives.png" alt="FunHives" className="wh-logo" />
        <p className="wh-tagline">PLAY &middot; KNOW &middot; CREATE</p>

        {/* Badge */}
        <div className="wh-badge">
          <span>🏆</span> 2M+ Quiz Creators <span>🐝</span>
        </div>

        {/* Game category buttons */}
        <div className="wh-categories">
          <button
            className="wh-cat wh-cat-quiz"
            onClick={() => navigate('/join')}
          >
            <span className="wh-cat-icon">⭐</span>
            <span className="wh-cat-label">Smart Quiz</span>
          </button>
          <button
            className="wh-cat wh-cat-geo"
            onClick={() => navigate('/join')}
          >
            <span className="wh-cat-icon">⊙</span>
            <span className="wh-cat-label">Geo Guess</span>
          </button>
          <button
            className="wh-cat wh-cat-drawing"
            onClick={() => navigate('/join')}
          >
            <span className="wh-cat-icon">⚙️</span>
            <span className="wh-cat-label">Sketcher</span>
          </button>
        </div>

        <p className="wh-join-text">Join over 2M quiz creators worldwide</p>

        {/* CTA buttons */}
        <button
          className="wh-btn wh-btn-primary"
          onClick={() => navigate('/signup')}
        >
          Sign up — It's Free 😊
        </button>
        <button
          className="wh-btn wh-btn-secondary"
          onClick={() => navigate('/login')}
        >
          Sign In
        </button>

        {/* Divider */}
        <div className="wh-divider">
          <span>or continue with:</span>
        </div>

        {/* Google button — real OAuth, same flow as the /login page */}
        <GoogleOAuthProvider clientId={google_sign_client_id || ''}>
          <div className="wh-google-wrapper">
            <GoogleLogin
              onSuccess={handleLoginSuccess}
              onError={handleLoginFailure}
              width="400"
            />
          </div>
        </GoogleOAuthProvider>

        {errorMessage && (
          <div className="auth-error-message">{errorMessage}</div>
        )}

        {/* Tip */}
        <div className="wh-tip">
          <span className="wh-tip-icon">✨</span>
          <p>Tip: Generate quizzes on any topic in seconds. No prep needed!</p>
        </div>
      </div>

      {isLoading && <Loading message="Authenticating..." />}
    </div>
  );
};

export default WebsiteHome;
